/**
 * Format: Menu
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API menu responses.
 *
 */
import { specialLinks } from '~/config/navigations'
import { convertToHandle } from '~/helpers/convert'

/**
 * Format menu.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Menu
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - Menu response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    /**
     * Storefront API URL always uses .myshopify.com domain.
     */
    const urlPath =
      object.url.split(cnvs.store.localisedShopDomain)[1] ||
      object.url.split(cnvs.store.localisedPermanentDomain)[1]

    const isHomepage =
      object.url.replace('https:', '') === cnvs.store.localisedShopDomain ||
      object.url.replace('https:', '') === cnvs.store.localisedPermanentDomain

    const url = urlPath || isHomepage
      ? `//${location.host}${cnvs.routes.normalisedRoot}${urlPath ? urlPath : ''}`
      : object.url

    /**
     * Return formatted data.
     */
    return {
      handle,
      links,
      tier,
      title: object.title,
      url,
    }
  })
}

/**
 * Rebuild menu items with speical links.
 * @param {Array} items - Menu items
 * @returns {Array}
 */
export function formatMenuWithCols({
  prefix = 'mobile',
  items,
}) {
  return items
    .map((item) => {
      const {
        handle,
        links,
        tier,
        title,
        url,
      } = item

      const isColor = title.includes(specialLinks.color)
      const isBanner = title.includes(specialLinks.banner)
      if (isColor || isBanner) {
        return false
      }

      if (!links) {
        return item
      }

      /**
       * Set color as link's own property.
       */
      const color = getColor(links)

      /**
       * Set promotions as link's own property.
       */
      const promotions = getPromotions(links)

      /**
       * Check if link has columns.
       * - If yes, convert columns into child links.
       * - Else, return current menu item.
       */
      const hasCols = links.find((childLink) => {
        return childLink.title.includes(specialLinks.col)
      })

      if (!hasCols) {
        return {
          color,
          handle,
          links: links ? formatMenuWithCols({ items: links }) : [],
          promotions,
          tier,
          title,
          url,
        }
      }

      /**
       * Return menu with converted columns.
       */
      return {
        color,
        handle,
        links: getColumns(prefix, item),
        promotions,
        tier,
        title,
        url,
      }
    })
    .filter(Boolean)
}

/**
 * Get color of the tier.
 * @param {Array} links - Child links of the tier.
 * @returns {String}
 */
function getColor(links) {
  let color = ''

  for (const link of links) {
    if (!link.title.includes(specialLinks.color)) {
      continue
    }

    color = link.title.replace(specialLinks.color, '')
    break
  }

  return color
}

/**
 * Get promotions of the tier.
 * @param {Array} links - Child links of the tier.
 * @returns {Array}
 */
function getPromotions(links) {
  return links
    .filter((childLink) => {
      return childLink.title.includes(specialLinks.banner)
    })
    .map((banner) => {
      const titleArray = banner.title
        .replace(specialLinks.banner, '')
        .split('|')
      return {
        handle: banner.handle,
        image: titleArray[0],
        title: titleArray.length > 1 ? titleArray[1] : '',
        url: banner.url,
      }
    })
}

/**
 * Get columns of the tier.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {Object} links - Current menu item.
 * @returns {Array}
 */
function getColumns(prefix, item) {
  const {
    handle,
    links,
    tier,
  } = item
  let currentColumn = -1
  const linkColumns = []

  links.forEach((childLink) => {
    const isCol = childLink.title.includes(specialLinks.col)
    const isHeading = childLink.title.includes(specialLinks.heading)

    if (!linkColumns.length && !isCol) {
      return
    }

    if (isCol) {
      currentColumn++
      const childTitle = childLink.title
      const childTier = tier + 1
      const childHandle = `${prefix}-${handle}-${convertToHandle(childTitle)}-${childTier}-${currentColumn}`
      linkColumns.push({
        handle: childHandle,
        links: [],
        tier: childTier,
        title: '',
        url: childLink.url,
      })
      return
    }

    if (isHeading) {
      const heading = childLink.title.replace(specialLinks.heading, '')
      linkColumns[currentColumn].title = heading
      return
    }

    linkColumns[currentColumn].links.push({
      ...childLink,
      tier: childLink.tier + 1,
    })
  })

  if (linkColumns.length === 1 && !linkColumns[0].title) {
    return linkColumns[0].links
      .map((link) => {
        return {
          ...link,
          tier: link.tier - 1,
        }
      })
  }

  return linkColumns
}
